import React, { useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  Position,
} from 'reactflow';

type HandoffEdgeData = {
  ruleId: string;
  label: string;
  pathOffset?: number;
  routeKind?: 'forward' | 'back' | 'skip' | 'branch';
};

const handleIdToPosition = (handleId?: string | null): Position => {
  if (handleId?.endsWith('top')) return Position.Top;
  if (handleId?.endsWith('bottom')) return Position.Bottom;
  if (handleId?.endsWith('left')) return Position.Left;
  return Position.Right;
};

/** Shallow U-turn under a chain — keeps cycle edges visible with the label on-path. */
const buildBackEdgePath = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  pathOffset: number,
): { path: string; labelX: number; labelY: number } => {
  const span = Math.abs(targetX - sourceX);
  const depth = Math.min(96, Math.max(58, span * 0.14)) + Math.max(pathOffset - 48, 0) * 0.35;
  const troughY = Math.max(sourceY, targetY) + depth;
  const midX = (sourceX + targetX) / 2;
  return {
    path: `M ${sourceX},${sourceY} C ${sourceX},${troughY} ${targetX},${troughY} ${targetX},${targetY}`,
    labelX: midX,
    labelY: troughY - 10,
  };
};

export const HandoffEdge: React.FC<EdgeProps<HandoffEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourceHandleId,
  targetHandleId,
  data,
  markerEnd,
  style,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
}) => {
  const sourcePosition = handleIdToPosition(sourceHandleId);
  const targetPosition = handleIdToPosition(targetHandleId);
  const pathOffset = data?.pathOffset ?? 0;
  const routeKind = data?.routeKind ?? 'forward';
  const distance = Math.hypot(targetX - sourceX, targetY - sourceY);
  const sameSide = sourcePosition === targetPosition;

  const { edgePath, labelX, labelY } = useMemo(() => {
    if (routeKind === 'back') {
      const back = buildBackEdgePath(sourceX, sourceY, targetX, targetY, pathOffset);
      return { edgePath: back.path, labelX: back.labelX, labelY: back.labelY };
    }

    const useSmoothStep = sameSide && distance < 280;
    const curvature =
      routeKind === 'skip'
        ? 0.42
        : routeKind === 'branch'
          ? 0.28
          : 0.16 + Math.min(Math.abs(pathOffset), 60) / 500;

    if (useSmoothStep) {
      const [path, x, y] = getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: 16,
        offset: 26 + Math.abs(pathOffset),
      });
      return { edgePath: path, labelX: x, labelY: y };
    }

    const [path, x, y] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      curvature,
    });
    return { edgePath: path, labelX: x, labelY: y + pathOffset * 0.12 };
  }, [
    distance,
    pathOffset,
    routeKind,
    sameSide,
    sourcePosition,
    sourceX,
    sourceY,
    targetPosition,
    targetX,
    targetY,
  ]);

  const label = data?.label;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              padding: `${labelBgPadding?.[1] ?? 4}px ${labelBgPadding?.[0] ?? 8}px`,
              borderRadius: labelBgBorderRadius ?? 8,
              color: (labelStyle?.fill as string) || undefined,
              background: (labelBgStyle?.fill as string) || '#fff',
              border: `1px solid ${(labelBgStyle?.stroke as string) || '#d0d5dd'}`,
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
              maxWidth: 200,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

export { buildBackEdgePath };
